import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeDrivers } from 'Core/hooks';
import { useIntl } from 'react-intl';

import {
    Modal,
    Button,
    IconButton,
    TimeFrame,
    useTimeFrameComponent,
    MenuComponent,
    Checkbox,
    Loading,
    Select,
    InfiniteListLoader
} from '@gpstrackit/gtc-react-controls';

import { Timezone } from 'Components/uiControls'

import moment from 'moment';
import { Alert, Grid, Typography } from '@mui/material';

import { NotificationManager } from 'Components/Notifications';
import DriverStatusLogCreateEdit from './components/createEdit';
import DeleteDriverStatusLog from './components/delete';
import { GetFormatForMoment, formatTimeHour } from 'Core/data/Helpers';

import './styles.scss';

//actions
import {
    reset,
    closeListModal,
    searchDriverStatusLog
} from './redux/slice';

import {
    openCreateEditModal
} from './components/createEdit/redux/slice';

import {
    openDeleteModal
} from './components/delete/redux/slice';
import { DEFAULT_DRIVER_STATUS_ACTIONS } from './constants';


const DriverStatusLogList = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const defaultFrame = "today";

    const timeFrameOptions = [
        { id: "today", label: messages['date_last_today'] },
        { id: "yesterday", label: messages['date_last_yesterday'] },
        { id: "last7days", label: messages['date_last_7days'] },
        { id: "last30Days", label: messages['date_last_30days'] },
        { id: "custom", label: messages['date_custom'] },
    ];

    const timeFrame = useTimeFrameComponent(timeFrameOptions, defaultFrame);
    const { startDate, endDate } = timeFrame;

    const open = useSelector(state => state.driverTimeClockSliceRedux.open);
    const loading = useSelector(state => state.driverTimeClockSliceRedux.loading);
    const data = useSelector(state => state.driverTimeClockSliceRedux.data);
    const error = useSelector(state => state.driverTimeClockSliceRedux.error);
    const filters = useSelector(state => state.driverTimeClockSliceRedux.filters);

    //Get the drivers from the catalog
    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });

    const { dateformat, timeZone, durationFormat } = useSelector((state) => state.securityRedux.user);

    const [errorMessage, setErrorMessage] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [driver, setDriver] = useState();
    const [groupedData, setGroupedData] = useState({});

    useEffect(() => {
        if (open && error) {
            setErrorMessage(error?.error || messages['errorMessage']);
            dispatch(reset({ error: false, data: { items: [], total: 0 } }));
        }
    }, [error, open]);

    useEffect(() => {
        if (startDate && endDate && driver?.driverId) {
            getData();
        }
    }, [startDate, endDate, driver?.driverId]);

    useEffect(() => {
        if (data?.items) {
            groupDataPerDay();
        }
    }, [data?.items]);

    const groupDataPerDay = useCallback(() => {
        const { items } = data;
        let groupedData = {};
        items?.forEach(item => {
            let currentDate = moment(item?.statusLogOn).format("ll");
            if (!groupedData[currentDate]) {
                groupedData[currentDate] = {
                    title: currentDate,
                    total: item?.driverStatus?.accumulateTime ? item?.durationSeconds : 0,
                    items: []
                };
            } else {
                groupedData[currentDate].total = groupedData[currentDate].total + item?.durationSeconds;
            }

            groupedData[currentDate].items.push(item);
        });
        
        setGroupedData(groupedData);
    });

    const getData = useCallback(() => {
        const { startDate: currentStartDate, endDate: currentEndDate } = timeFrame;

        dispatch(searchDriverStatusLog({
            ...filters,
            openSearch: "",
            reset: true,
            driverId: driver?.driverId,
            dateRange: {
                start: currentStartDate.toISOString(),
                end: currentEndDate.toISOString()
            }
        }));
    });

    const handleClose = useCallback(() => {
        setErrorMessage("");
        dispatch(closeListModal());
        dispatch(reset());
        setDriver();
    });

    const formatDailyTotal  = (date) => {
        let hours = 0

        if(!date) return `${hours} ${messages["reportsMainRT_hours"]}`
        if(durationFormat !== "DDhhmmss"){
            hours = Number.parseFloat(date/3600).toFixed(2);
            return `${hours} ${messages["reportsMainRT_hours"]} `
        }else{
            const days = Math.floor(date / (24 * 60 * 60));
            date -= days * (24 * 60 * 60);
            const hours = Math.floor(date / (60 * 60));
            date -= hours * (60 * 60);
            const minutes = Math.floor(date / 60);
            date -= hours * (60 * 60);
            date -= minutes * 60;

            let result = '';
            if (days > 0) {
                result += `${days} ${messages['settingsDriverStatusLog_day']}${days > 1 ? 's' : ''} `;
            }
            if (hours > 0) {
                result += `${hours} ${messages['settingsDriverStatusLog_hour']}${hours > 1 ? 's' : ''} `;
            }
            if (minutes > 0) {
                result += `${minutes} ${messages['settingsDriverStatusLog_minute']}${minutes > 1 ? 's' : ''} `;
            }
            if (date > 0) {
                result += `${date} ${messages['settingsDriverStatusLog_second']}${date > 1 ? 's' : ''} `;
            }

            return result.trim();
        }
        
    }

    const isDateBetween = (date) => {
        const { startDate, endDate } = timeFrame;
        const dateMoment = moment(date);
        const startMoment = moment(startDate);
        const endMoment = moment(endDate);
        return dateMoment.isBetween(startMoment, endMoment, 'day', '[]');
    }


    let title = messages['settingsDriverStatusLog_title'];

    return (
        <Modal
            title={title}
            open={open}
            loading={loading}
            position="right"
            size="small"
            handleClose={handleClose}
            footer={(
                <div style={{ display: 'flex', alignItems: "center", justifyContent: 'end', width: '100%' }}>
                    <Button
                        disabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant='secondary'
                    >
                        {messages['close']}
                    </Button>
                </div>
            )}
        >
            <div className='time-card-container'>
                <div>
                    <TimeFrame
                        {...timeFrame}
                        label={messages['settingsDriverStatusLog_timeFrame']}
                        placeholder={messages['settingsDriverStatusLog_timeFrame']}
                        customRangeDate={90}
                        maxDate={moment()}
                        filterSwitch={false}
                        withTime={true}
                        dateFormat={dateformat || "MM/DD/YYYY hh:mm A"}
                    />
                </div>

                <div
                    style={{
                        marginBottom: 5
                    }}
                >
                    <Select
                        value={driver}
                        label={messages['settingsDriverStatusLog_driver']}
                        options={drivers}
                        onChange={(newDriver) => {
                            setDriver(newDriver);
                        }}
                        isMulti={false}
                        textField="name"
                        valueField="driverId"
                        isSearchable
                    />
                </div>

                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                >
                    <Grid item>
                        <IconButton
                            icon="add_box"
                            tooltip={messages['add']}
                            isDisabled={loading || !driver?.driverId}
                            style={{ color: 'black' }}
                            size="small"
                            onClick={() => {
                                dispatch(openCreateEditModal({ driver: driver, lastLog: data?.items?.length ? data?.items[0] : null }));
                            }}
                        />
                    </Grid>

                    {
                        selectedRows?.length > 0 &&
                        <Grid item>
                            <IconButton
                                icon="delete"
                                isDisabled={!selectedRows?.length || loading}
                                tooltip={messages['delete']}
                                size="small"
                                style={{ color: 'black', marginLeft: 3 }}
                                onClick={() => {
                                    dispatch(openDeleteModal(selectedRows));
                                }}
                            />
                        </Grid>
                    }

                </Grid>

                <div className='time-card-list-container'>
                    <Grid
                        container
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                    >
                        {
                            loading &&
                            <Loading full />
                        }

                        {
                            groupedData &&Object.keys(groupedData)?.map((key) => {
                                if(!isDateBetween(groupedData[key]?.items[0]?.createdOn)) return null;
                                return (
                                    <Fragment key={key}>
                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            style={{ width: '100%' }}
                                        >
                                            <Grid item>
                                                <h4>{key}</h4>
                                            </Grid>
                                            <Grid item tyle={{ textAlign: 'right' }}>
                                                <h4>{messages['settingsDriverStatusLog_dailyTotal']} : {formatDailyTotal(groupedData[key]?.total)}</h4>
                                            </Grid>
                                        </Grid>

                                        {
                                            groupedData[key]?.items?.length > 0 && groupedData[key]?.items?.map((item, index) => {
                                                let selected = selectedRows.find(selectedRow => selectedRow.id === item.id);
                                                return (
                                                    <Grid
                                                        key={item?.id || `${key}-${index}`}
                                                        item={true}
                                                        xs
                                                        style={{ width: '100%', margin: 0 }}
                                                    >
                                                        <div className={`time-card ${selected ? "time-card-selected" : ""}`}>
                                                            <Grid
                                                                container
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                className="context-menu-hover-container"
                                                                wrap="nowrap"
                                                            >
                                                                {/** 
                                                                <Grid item={true}>
                                                                    <Checkbox
                                                                        id={`time-card-${item?.id}`}
                                                                        value={selected ? true : false}
                                                                        checked={selected ? true : false}
                                                                        size="sm"
                                                                        onChange={(checked) => {
                                                                            let newSelectedRows = [];
                                                                            if (checked) {
                                                                                newSelectedRows = [...selectedRows];
                                                                                newSelectedRows.push(item);
                                                                            } else {
                                                                                newSelectedRows = selectedRows.filter(selectedRow => selectedRow.id !== item.id);
                                                                            }
                                                                            setSelectedRows([...newSelectedRows]);
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                */}

                                                                <Grid zeroMinWidth xs item={true}>
                                                                    <Typography
                                                                        noWrap
                                                                        variant="subtitle1"
                                                                        className="ime-card-title"
                                                                    >
                                                                        {DEFAULT_DRIVER_STATUS_ACTIONS[item?.driverStatus?.id] ? messages[DEFAULT_DRIVER_STATUS_ACTIONS[item?.driverStatus?.id]] : item?.driverStatus?.name}
                                                                    </Typography >
                                                                </Grid>

                                                                <Grid item={true}>
                                                                    <MenuComponent
                                                                        isDisable={loading}
                                                                        options={[
                                                                            messages['edit'],
                                                                            messages['delete']
                                                                        ]}
                                                                        placement="right"
                                                                        icon={'more_vert'}
                                                                        className="context-menu-hover"
                                                                        onClick={(type, value) => {
                                                                            if (type === messages['edit']) {
                                                                                dispatch(openCreateEditModal({ driverStatusLog: item, driver: driver }));
                                                                            } else if (type === messages['delete']) {
                                                                                dispatch(openDeleteModal([value]));
                                                                            }
                                                                        }}
                                                                        value={item}
                                                                    />
                                                                </Grid>
                                                            </Grid>

                                                            <Grid
                                                                container
                                                                direction="row"
                                                                justifyContent="flex-start"
                                                                alignItems="center"
                                                            >
                                                                <Grid item>
                                                                    <Typography noWrap variant="subtitle2" color={'textSecondary'} style={{ float: 'left', marginLeft: 3, marginRight: 10 }}>
                                                                        {messages['settingsDriverStatusLog_date']}:
                                                                    </Typography>

                                                                    <Timezone
                                                                        date={item?.statusLogOn}
                                                                        dateFormat={dateformat}
                                                                        timeFormat="hh:mm a"
                                                                        timezone={timeZone}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </div>
                                                    </Grid>
                                                );
                                            })
                                        }
                                    </Fragment>
                                )
                            })
                        }
                    </Grid>
                </div>
            </div>


            <DriverStatusLogCreateEdit
                refresh={() => {
                    getData();
                }}
            />

            <DeleteDriverStatusLog
                refresh={() => {
                    getData();
                }}
            />
        </Modal>
    );
}

export default DriverStatusLogList;